import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemCreationToolTip } from '~/Components';
import { ExtendedLocation } from '../ExtendedLocation.model';
import { EventCancellation } from '~/Components/Icons';
import { Switcher, TextField } from '~/UIKit';
import { makeStyles } from '~/UIKit/Style';
import {
  CHARCOAL_GREY,
  FADED_ICON_ERROR,
  GRAY_BACKGROUND,
  LABEL_COLOR,
  LIGHT_BLUE,
  PRIMARY_BLUE,
  VERTICAL_SEPARATOR,
} from '~/Constants/Colors.constants';
import { FONT_SIZE_LARGE, FONT_SIZE_SMALL } from '~/Constants/UI.constants';
import { connect } from 'formik';
import { useRequestFirstFreeCheckin } from '~/requests/providerService/useRequestFirstFreeCheckin/useRequestFirstFreeCheckin';
import { AccessList, useHasAccess, useNavigation } from '~/Hooks';
import { FreeCheckin } from '~/Components/Icons/FreeCheckin';

const useStyles = (hasError = false, isDisabled = false) =>
  makeStyles(
    (theme) => ({
      container: {
        [theme.breakpoints.up('md')]: {
          paddingTop: 15,
        },
        '&:not(:first-of-type)': {
          paddingTop: 30,
        },
      },
      title: {
        height: 40,
        display: 'flex',
        alignItems: 'center',
        padding: '0 15px',
        fontSize: FONT_SIZE_SMALL,
        fontWeight: 'bold',
        letterSpacing: '1px',
        lineHeight: '16px',
        color: LABEL_COLOR,
        textTransform: 'uppercase',
        lineBreak: 'anywhere',
        overflow: 'hidden',
      },
      inputsContainer: {
        color: CHARCOAL_GREY,
        width: '100%',
        backgroundColor: isDisabled ? GRAY_BACKGROUND : 'inherit',
        display: 'inherit',
        pointerEvents: isDisabled ? 'none' : 'initial',
        borderTop: `thin solid ${VERTICAL_SEPARATOR}`,
      },
      onlineCancellationInputContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: 50,
        padding: '0 15px',
        position: 'relative',
        paddingLeft: 15,
      },
      onlineCancellationLabelIcon: {
        backgroundColor: LIGHT_BLUE,
        borderRadius: 16,
        minWidth: 32,
        maxWidth: 32,
        minHeight: 32,
        maxHeight: 32,
        lineHeight: '15px',
        fontSize: FONT_SIZE_LARGE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
        color: CHARCOAL_GREY,
        '& > svg': {
          fontSize: 'inherit',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
      onlineCancellationToggle: {
        marginLeft: 'auto',
        color: PRIMARY_BLUE,
        '& input': {
          textAlign: 'right',
        },
      },
      tooltipText: {
        textAlign: 'left',
      },
      cancellationDeadlineInput: {
        padding: '0 15px',
        backgroundColor: hasError ? FADED_ICON_ERROR : 'initial',
      },
    }),
    { name: 'PartnetLocationSettings' },
  );

const minValue = 1;
const maxValue = 12;

const WithFormikContext = connect<{ initialValue: number }, ExtendedLocation>(
  ({ initialValue, formik }) => {
    const { values, isSubmitting, setFieldValue } = formik;
    const { getSearchParams } = useNavigation();
    const { id } = getSearchParams();
    const { hasAccess } = useHasAccess();

    const canViewSettingsTab = hasAccess(AccessList.DEFAULT_CANCELATION_WINDOW);

    const { data: firstFreeCheckinResponse } = useRequestFirstFreeCheckin(id, canViewSettingsTab);
    const containerRef = useRef(null);

    const currentValue = values.defaultCancellationWindow;
    const isInvalid =
      currentValue !== null && (+currentValue < minValue || +currentValue > maxValue);

    const { classes } = useStyles(isInvalid, false)();
    const { t } = useTranslation();

    const setCancellationWindow = (value: typeof currentValue) =>
      setFieldValue('defaultCancellationWindow', value);

    const isInputVisible = currentValue !== null;
    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      setCancellationWindow(e.target.value === '' ? null : +e.target.value);
    }

    function toggleCancellationWindow() {
      if (!isInputVisible) {
        setCancellationWindow(initialValue || 0);
      } else {
        setCancellationWindow(null);
      }
    }

    useEffect(() => {
      if (firstFreeCheckinResponse?.locationSetting?.isFirstCheckInFree !== undefined) {
        setFieldValue(
          'firstFreeCheckin',
          firstFreeCheckinResponse.locationSetting.isFirstCheckInFree,
        );
      }
    }, [firstFreeCheckinResponse, setFieldValue]);

    return (
      <div className={classes.container} ref={containerRef}>
        <div className={classes.title} data-testid="cancellationTooltip">
          <span>{t('app.defaultCancellationWindow.cancellation')}</span>
          <ItemCreationToolTip
            title={t('app.defaultCancellationWindow.cancellationTooltip')}
            placement="bottom"
            containerRef={containerRef}
            textClassName={classes.tooltipText}
          />
        </div>
        <div className={classes.inputsContainer}>
          <div className={classes.onlineCancellationInputContainer}>
            <div className={classes.onlineCancellationLabelIcon}>
              <EventCancellation />
            </div>
            <label
              htmlFor="toggleDefaultCancellationWindow"
              className={classes.ItemCreationSubsectionTypographyBody}
            >
              {t('app.defaultCancellationWindow.onlineCancellation')}
            </label>
            <Switcher
              id="toggleDefaultCancellationWindow"
              checked={isInputVisible}
              onChange={toggleCancellationWindow}
              className={classes.onlineCancellationToggle}
              disabled={isSubmitting}
            />
          </div>
          {isInputVisible && (
            <TextField
              type="number"
              className={classes.cancellationDeadlineInput}
              InputProps={{ inputProps: { min: minValue, max: maxValue } }}
              value={currentValue}
              onChange={handleChange}
              name="defaultCancellationWindow"
              label={t('app.defaultCancellationWindow.cancellationDeadline')}
              disabled={isSubmitting}
              fullWidth
              newDesign
            />
          )}
        </div>

        <div className={classes.title} data-testid="firstFreeCheckinTooltip">
          <span>{t('app.firstFreeCheckin.title')}</span>
          <ItemCreationToolTip
            title={t('app.firstFreeCheckin.tooltip')}
            placement="bottom"
            containerRef={containerRef}
            textClassName={classes.tooltipText}
          />
        </div>
        <div className={classes.inputsContainer}>
          <div className={classes.onlineCancellationInputContainer}>
            <div className={classes.onlineCancellationLabelIcon}>
              <FreeCheckin />
            </div>
            <label
              htmlFor="toggleFirstFreeCheckin"
              className={classes.ItemCreationSubsectionTypographyBody}
            >
              {t('app.firstFreeCheckin.enable')}
            </label>
            <Switcher
              id="toggleFirstFreeCheckin"
              checked={values.firstFreeCheckin}
              onChange={(e) => setFieldValue('firstFreeCheckin', e.target.checked)}
              className={classes.onlineCancellationToggle}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>
    );
  },
);

export const LocationEditSettingsSection = (initialValue: number) => (
  <WithFormikContext initialValue={initialValue} />
);
